import React from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Tag, Card, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useCohortsByUserId, useCohortSessions } from "../../api/CohortsAPI";
import {
  agGridThemeVars,
  agGridDefaultColDef,
  agGridPinnedActionsCellStyle,
} from "../../constants/agGridTheme";

const ParticipantsCell = ({ cohortId, status }) => {
  const { data: sessions, isLoading } = useCohortSessions(
    status === "not_started" ? null : cohortId
  );

  if (status === "not_started") {
    return <span>-</span>;
  }

  if (isLoading) {
    return <Spin size="small" />;
  }

  const participantCount = new Set((sessions || []).map((s) => s.user_id)).size;

  return <span>{participantCount}</span>;
};

const CohortsTable = ({ userId }) => {
  const navigate = useNavigate();
  const {
    data: cohorts,
    isLoading: cohortsLoading,
    error: cohortsError,
  } = useCohortsByUserId(userId);

  const manageCohortRenderer = (params) => {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button
          onClick={() => navigate(`/cohorts/${params.data.id}`)}
          className="px-3 py-1 rounded-md text-xs font-medium transition-colors duration-200 shadow-sm flex items-center justify-center h-7 bg-primary hover:bg-primaryLight text-white hover:shadow"
          title="Manage this cohort"
        >
          <span className="text-center">Manage Cohort</span>
        </button>
      </div>
    );
  };

  const nameRenderer = (params) => {
    if (!params.value) {
      return <i>Untitled Cohort</i>;
    }
    return params.value;
  };

  const statusRenderer = (params) => {
    const statusConfig = {
      not_started: { color: "warning", text: "Not Started" },
      active: { color: "success", text: "Active" },
      archived: { color: "default", text: "Archived" },
    };

    const status = params.value;
    const config = statusConfig[status] || { color: "default", text: status };

    return (
      <div className="flex items-center justify-center w-full h-full">
        <Tag color={config.color} className="!text-xs">
          {config.text}
        </Tag>
      </div>
    );
  };

  const columnDefs = [
    { headerName: "ID", field: "id", width: 90 },
    {
      headerName: "Name",
      field: "name",
      minWidth: 200,
      flex: 1,
      cellRenderer: nameRenderer,
    },
    {
      headerName: "Created At",
      field: "created_at",
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
      width: 160,
    },
    {
      headerName: "Status",
      field: "status",
      width: 130,
      cellRenderer: statusRenderer,
    },
    {
      headerName: "Participants",
      width: 130,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <ParticipantsCell cohortId={params.data.id} status={params.data.status} />
      ),
    },
    {
      headerName: "Actions",
      cellRenderer: manageCohortRenderer,
      sortable: false,
      filter: false,
      pinned: "right",
      width: 170,
      cellStyle: agGridPinnedActionsCellStyle,
    },
  ];

  if (cohortsLoading) {
    return <div>Loading...</div>;
  }

  if (cohortsError) {
    return <div>Error loading cohorts: {cohortsError.message}</div>;
  }

  return (
    <Card className="w-full">
      <div
        className="ag-theme-alpine"
        style={{
          height: 400,
          width: "100%",
          ...agGridThemeVars,
        }}
      >
        <AgGridReact
          rowData={cohorts}
          columnDefs={columnDefs}
          defaultColDef={agGridDefaultColDef}
          pagination={true}
          paginationPageSize={10}
          domLayout="autoHeight"
        />
      </div>
    </Card>
  );
};

export default CohortsTable;